import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { Udalost } from '../api/JsdhNotifikaceService/generated/models/Udalost';
import type { Odpoved } from '../api/JsdhNotifikaceService/generated/models/Odpoved';
import type { PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import type { UdalostRouteResponse } from '../api/JsdhNotifikaceService/generated/models/UdalostRoute';
import type { PoplachConfig } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';
import { Vyjezd2026Adresa, Vyjezd2026Hlavicka, Vyjezd2026PosilaZpravy, maPosilaZpravy } from './Vyjezd2026InfoPanel';
import { Vyjezd2026MapaSection, mapaJeViditelna } from './Vyjezd2026MapaSection';
import { Vyjezd2026TechnikaSection, type TechnikaCard } from './Vyjezd2026TechnikaSection';
import type { ZalohaJinde } from '../api/JsdhNotifikaceService/generated/models/ZalohaJinde';
import { Vyjezd2026OdpovediSection } from './Vyjezd2026OdpovediSection';
import { velikostCasovkyPx } from './Vyjezd2026CasovkaOverlay';
import { vyskySekciNaVysku } from '../utils/vyskySekciNaVysku';

/** Referenční výška kiosku na výšku; použije se, dokud kontejner není změřený (jsdom). */
export const REFERENCNI_VYSKA_NA_VYSKU = 1920;
/** Banner „VZOROVÝ POPLACH" (subtitle1 28 px + py 2 × 4 px) zaokrouhlený nahoru. */
export const VYSKA_BANNERU_PX = 40;

export interface Vyjezd2026NaVyskuLayoutProps {
    aktivniPoplach: Udalost;
    notifikaceLog: PosilovaNotifikaceLogResponse[];
    odpovedi: Odpoved[];
    /** Lide v zaloze u jineho aktivniho poplachu (GET .../zaloha-jinde). */
    zalohaJinde?: ZalohaJinde[];
    technikaCards: TechnikaCard[];
    routeData: UdalostRouteResponse | null;
    stationGps?: string;
    casVyjezdMinuty: number;
    poplachConfig: PoplachConfig;
    deviceToken?: string;
    isPreview?: boolean;
}

// offsetHeight prvku přes ResizeObserver, v nezmenšených px (transform: scale náhledu ho neovlivní).
const useVyskaPrvku = (ref: React.RefObject<HTMLElement | null>): number => {
    const [vyska, setVyska] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const zmer = () => setVyska(el.offsetHeight);
        zmer();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(zmer);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return vyska;
};

const sekceSx = (vyska: number) => ({
    height: `${vyska}px`, flex: 'none', minHeight: 0, overflow: 'hidden', display: 'flex',
    borderBottom: '2px solid', borderColor: 'divider', boxSizing: 'border-box',
} as const);

// Rozložení A2 (spec kiosek na výšku, kap. 3): událost → adresa → mapy → technika →
// posila/zprávy → semafor → jména. Výšky sekcí v px z výšky KONTEJNERU, žádné vh.
export const Vyjezd2026NaVyskuLayout: React.FC<Vyjezd2026NaVyskuLayoutProps> = ({
    aktivniPoplach, notifikaceLog, odpovedi, zalohaJinde, technikaCards, routeData, stationGps,
    casVyjezdMinuty, poplachConfig, deviceToken, isPreview,
}) => {
    const korenRef = useRef<HTMLDivElement>(null);
    const mereniAdresyRef = useRef<HTMLDivElement>(null);
    const mereniPosilRef = useRef<HTMLDivElement>(null);
    const vyskaKontejneru = useVyskaPrvku(korenRef);
    // Přirozená výška obsahu při maximálním písmu ze SKRYTÝCH kopií – ne z vykreslené sekce,
    // jinak by vznikla zpětná vazba (oscilace, „ResizeObserver loop").
    const obsahAdresy = useVyskaPrvku(mereniAdresyRef);
    const obsahPosil = useVyskaPrvku(mereniPosilRef);

    const mapaLayout = poplachConfig.mapaLayout || 'misto';
    const maPosilyZpravy = maPosilaZpravy(aktivniPoplach, notifikaceLog);
    const vysky = vyskySekciNaVysku({
        vyskaKontejneru: vyskaKontejneru > 0 ? vyskaKontejneru : REFERENCNI_VYSKA_NA_VYSKU,
        pocetVozu: technikaCards.length,
        maPosilyZpravy,
        mapaViditelna: mapaJeViditelna(aktivniPoplach, mapaLayout, routeData),
        vyskaObsahuAdresy: obsahAdresy > 0 ? obsahAdresy : Infinity,
        vyskaObsahuPosilZprav: obsahPosil > 0 ? obsahPosil : Infinity,
    });
    // Banner vzorového poplachu zabírá horní část hlavičky; časovka se dimenzuje ze zbytku.
    const odsazeniBanneru = isPreview ? VYSKA_BANNERU_PX : 0;

    return (
        <Box ref={korenRef} data-testid="vyjezd-na-vysku" sx={{
            width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
            position: 'relative', overflow: 'hidden',
        }}>
            {/* Měřicí kopie zdvojují data-testid (vyska-adresa*, vyska-posila-zpravy, zprava-*),
                proto testy musí hledat uvnitř vyska-sekce-*, ne přes celý dokument. */}
            <Box aria-hidden="true" data-mereni="true" sx={{
                position: 'absolute', top: 0, left: 0, width: '100%',
                visibility: 'hidden', pointerEvents: 'none',
            }}>
                <Box ref={mereniAdresyRef}>
                    <Vyjezd2026Adresa aktivniPoplach={aktivniPoplach} rozlozeni="vyska" />
                </Box>
                <Box ref={mereniPosilRef}>
                    {maPosilyZpravy && (
                        <Vyjezd2026PosilaZpravy aktivniPoplach={aktivniPoplach} notifikace={notifikaceLog} rozlozeni="vyska" />
                    )}
                </Box>
            </Box>

            <Box data-testid="vyska-sekce-hlavicka" sx={sekceSx(vysky.hlavicka)}>
                <Vyjezd2026Hlavicka aktivniPoplach={aktivniPoplach} rozlozeni="vyska"
                    casVyjezdMinuty={casVyjezdMinuty}
                    casovkaPx={velikostCasovkyPx(vysky.hlavicka, odsazeniBanneru)}
                    odsazeniShoraPx={odsazeniBanneru} />
            </Box>
            <Box data-testid="vyska-sekce-adresa" sx={sekceSx(vysky.adresa)}>
                <Vyjezd2026Adresa aktivniPoplach={aktivniPoplach} rozlozeni="vyska" />
            </Box>
            <Box data-testid="vyska-sekce-mapy" sx={sekceSx(vysky.mapy)}>
                <Vyjezd2026MapaSection
                    aktivniPoplach={aktivniPoplach}
                    mapConfig={poplachConfig.mapa}
                    mapaLayout={mapaLayout}
                    routeData={routeData}
                    stationGps={stationGps}
                    casVyjezdMinuty={casVyjezdMinuty}
                    casovkaOverlay={false}
                />
            </Box>
            <Box data-testid="vyska-sekce-technika" sx={sekceSx(vysky.technika)}>
                <Vyjezd2026TechnikaSection cards={technikaCards} deviceToken={deviceToken} mrizka="vyska" />
            </Box>
            {maPosilyZpravy && (
                <Box data-testid="vyska-sekce-posila-zpravy" sx={sekceSx(vysky.posilaZpravy)}>
                    <Vyjezd2026PosilaZpravy aktivniPoplach={aktivniPoplach} notifikace={notifikaceLog} rozlozeni="vyska" />
                </Box>
            )}
            <Box data-testid="vyska-sekce-odpovedi" sx={{ ...sekceSx(vysky.semafor + vysky.jmena), borderBottom: 0 }}>
                <Vyjezd2026OdpovediSection
                    odpovedi={odpovedi}
                    zalohaJinde={zalohaJinde}
                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                    pravidla={poplachConfig.pravidlaVyjezdu}
                    showSpecializace={poplachConfig.zobrazitSpecializace !== false}
                    showFunkce={poplachConfig.zobrazitFunkce !== false}
                    rozlozeni="vyska"
                    vyskaSemaforuPx={vysky.semafor}
                />
            </Box>
        </Box>
    );
};
